import { useEffect } from "react";
import { useLocation } from "react-router-dom";

/**
 * Scrolls the window to the top whenever the route pathname changes on
 * public/marketing surfaces. Authenticated app routes (which sometimes
 * restore scroll positions intentionally, e.g. the Excerpts list) are
 * left alone.
 *
 * Hash navigations (#section) are skipped so anchor links still jump
 * to their targets.
 */
const MARKETING_PATHS = new Set([
  "/",
  "/pricing",
  "/auth",
  "/auth/finalize",
  "/reset-password",
  "/checkout/return",
  "/terms",
  "/privacy",
  "/cookies",
  "/unsubscribe",
]);

function isMarketingPath(pathname: string): boolean {
  const normalized = pathname.endsWith("/") && pathname !== "/"
    ? pathname.slice(0, -1)
    : pathname;
  if (MARKETING_PATHS.has(normalized)) return true;
  // Public profile pages /u/:username are marketing-ish too.
  if (normalized.startsWith("/u/")) return true;
  // Feature pages /features/:slug share the marketing layout.
  if (normalized.startsWith("/features/")) return true;
  // Help center pages — always land at the top on navigation.
  if (normalized === "/help" || normalized.startsWith("/help/")) return true;
  return false;
}

export const ScrollToTop = () => {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (hash) return;
    if (!isMarketingPath(pathname)) return;
    window.scrollTo(0, 0);
    const root = document.getElementById("app-scroll-root");
    if (root) root.scrollTop = 0;
    const inner = document.getElementById("app-scroll-container");
    if (inner) inner.scrollTop = 0;
  }, [pathname, hash]);
  return null;
};
